import { useTranslation } from "react-i18next";
import type { TAnalyticsErrorType } from "@/types/TAnalytics";

type ErrorAnalysisProps = {
  brokerErrors: TAnalyticsErrorType[];
  claimErrors: TAnalyticsErrorType[];
};

const ERROR_TYPE_LABELS: Record<string, string> = {
  invalid_vsnr: "Ungültige VSNR",
  invalid_plz: "Ungültige PLZ",
  invalid_schadennummer: "Ungültige Schadennummer",
  api_error: "API-Fehler",
  api_not_configured: "API nicht konfiguriert",
  not_found: "Nicht gefunden",
  auth_failed: "Authentifizierung fehlgeschlagen",
  internal_error: "Interner Fehler",
};

function ErrorBar({ type, count, total, color }: { type: string; count: number; total: number; color: string }) {
  const percentage = total > 0 ? (count / total) * 100 : 0;
  const label = ERROR_TYPE_LABELS[type] || type;

  return (
    <div className="space-y-1">
      <div className="flex justify-between text-sm">
        <span className="text-gray-600 dark:text-gray-400">{label}</span>
        <span className="text-gray-900 dark:text-white font-medium">
          {count} ({percentage.toFixed(1)}%)
        </span>
      </div>
      <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
        <div
          className={`h-full ${color} rounded-full transition-all duration-300`}
          style={{ width: `${percentage}%` }}
        />
      </div>
    </div>
  );
}

export function ErrorAnalysis({ brokerErrors, claimErrors }: ErrorAnalysisProps) {
  const { t } = useTranslation();

  const brokerFailed = brokerErrors.reduce((sum, e) => sum + e.count, 0);
  const claimFailed = claimErrors.reduce((sum, e) => sum + e.count, 0);

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-sm border border-gray-200 dark:border-gray-700">
      <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">
        {t("analytics.errors.title", "Fehleranalyse")}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Broker Errors */}
        <div>
          <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">
            {t("analytics.errors.broker", "Berater-Abfragen")} ({brokerFailed} {t("analytics.errors.failed", "fehlgeschlagen")})
          </h4>
          {brokerErrors.length > 0 ? (
            <div className="space-y-3">
              {brokerErrors.map((error) => (
                <ErrorBar
                  key={error.type}
                  type={error.type}
                  count={error.count}
                  total={brokerFailed}
                  color="bg-blue-500"
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("analytics.errors.noErrors", "Keine Fehler")}
            </p>
          )}
        </div>

        {/* Claim Errors */}
        <div>
          <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3">
            {t("analytics.errors.claim", "Schadenstatus-Abfragen")} ({claimFailed} {t("analytics.errors.failed", "fehlgeschlagen")})
          </h4>
          {claimErrors.length > 0 ? (
            <div className="space-y-3">
              {claimErrors.map((error) => (
                <ErrorBar
                  key={error.type}
                  type={error.type}
                  count={error.count}
                  total={claimFailed}
                  color="bg-green-500"
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t("analytics.errors.noErrors", "Keine Fehler")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
